import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, ShieldCheck, Clock, MessageCircle, Snowflake,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { BRAND, API } from "@/constants/brand";
import { lenisStore } from "@/lib/lenisStore";
import { trackLeadConversion } from "@/lib/analytics";

const EQUIPOS = [
  "1x1 (Split individual)",
  "2x1 (Multi-Split 2 estancias)",
  "3x1 o superior (Multi-Split)",
  "Conductos (Sistema centralizado)",
  "Otro / Necesito asesoramiento",
];

const INITIAL = {
  nombre: "",
  numero_equipos: "",
  aparato_comprado: "",
  ciudad: "",
  calle: "",
  codigo_postal: "",
  telefono: "",
  privacidad: false,
};

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-navy placeholder:text-slate-400 outline-none transition-all duration-300 focus:border-ocean-600 focus:ring-4 focus:ring-ocean-600/10";
const labelCls = "mb-2 block text-sm font-semibold text-navy";

const Field = ({ label, testid, error, children }) => (
  <div>
    <label className={labelCls}>{label}</label>
    {children}
    {error && (
      <p className="mt-1.5 text-xs font-medium text-red-600" data-testid={`${testid}-error`}>
        {error}
      </p>
    )}
  </div>
);

export default function Presupuesto() {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  useEffect(() => {
    lenisStore.current?.scrollTo(0, { immediate: true });
  }, []);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.nombre.trim()) er.nombre = "Indíquenos su nombre completo.";
    if (!form.numero_equipos) er.numero_equipos = "Seleccione el tipo de equipos.";
    if (!form.aparato_comprado) er.aparato_comprado = "Indique si ya tiene el aparato comprado.";
    if (!form.ciudad.trim()) er.ciudad = "Indíquenos su ciudad.";
    if (!form.calle.trim()) er.calle = "Indíquenos la calle y número.";
    if (!form.codigo_postal.trim()) er.codigo_postal = "Indíquenos el código postal.";
    if (!/^[+\d][\d\s-]{7,}$/.test(form.telefono.trim()))
      er.telefono = "Introduzca un número de teléfono válido.";
    if (!form.privacidad)
      er.privacidad = "Debe leer y aceptar la política de privacidad para continuar.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Revise los campos marcados antes de enviar.");
      return;
    }
    setSending(true);

    const message = [
      "NUEVA SOLICITUD DE PRESUPUESTO — CLIMA OLIVIER",
      "----------------------------------------",
      `Nombre: ${form.nombre.trim()}`,
      `Nº de equipos: ${form.numero_equipos}`,
      `Aparato ya comprado: ${form.aparato_comprado}`,
      `Ciudad: ${form.ciudad.trim()}`,
      `Calle: ${form.calle.trim()}`,
      `Código Postal: ${form.codigo_postal.trim()}`,
      `Teléfono: ${form.telefono.trim()}`,
    ].join("\n");

    try {
      await axios.post(`${API}/leads`, form);
    } catch {
      // El envío a WhatsApp continúa aunque falle el registro interno
    }

    window.open(
      `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(message)}`,
      "_blank",
      "noopener,noreferrer"
    );
    trackLeadConversion(form.numero_equipos);
    toast.success("Solicitud generada. Se ha abierto WhatsApp para enviarla a Clima Oliver.");
    setSending(false);
  };

  return (
    <div data-testid="presupuesto-page">
      <Navbar />
      <main className="bg-ocean-50/50 pb-20 pt-32 lg:pt-40">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-5 lg:px-12">
          <motion.aside
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative overflow-hidden rounded-3xl bg-navy p-8 text-white lg:col-span-2 lg:p-10"
            data-testid="presupuesto-info-panel"
          >
            <div className="grain-overlay pointer-events-none absolute inset-0" />
            <div className="pointer-events-none absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-ocean-600/25 blur-3xl" />
            <div className="relative">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-ocean-500">
                <Snowflake className="h-3.5 w-3.5" />
                Presupuesto automatizado
              </span>
              <h1 className="mt-6 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl text-balance">
                Solicitud de presupuesto automatizado
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-slate-300">
                Complete los datos del formulario y su solicitud llegará directamente al
                WhatsApp oficial de Clima Oliver, sin esperas ni intermediarios.
              </p>

              <ol className="mt-9 space-y-5">
                {[
                  "Complete el formulario en menos de 2 minutos",
                  "Se abrirá WhatsApp con su solicitud ya redactada",
                  "Pulse enviar y reciba respuesta personalizada",
                ].map((step, i) => (
                  <li key={step} className="flex items-start gap-4" data-testid={`presupuesto-step-${i + 1}`}>
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ocean-600 font-display text-xs font-bold">
                      {i + 1}
                    </span>
                    <p className="pt-1.5 text-sm text-slate-200">{step}</p>
                  </li>
                ))}
              </ol>

              <div className="mt-10 space-y-3 border-t border-white/10 pt-7 text-xs text-slate-300">
                <p className="flex items-center gap-2.5">
                  <ShieldCheck className="h-4 w-4 text-ember-500" />
                  2 años de garantía en la instalación
                </p>
                <p className="flex items-center gap-2.5">
                  <ShieldCheck className="h-4 w-4 text-ember-500" />
                  3 años de garantía en la máquina
                </p>
                <p className="flex items-center gap-2.5">
                  <Clock className="h-4 w-4 text-ocean-500" />
                  Respuesta en horario laboral el mismo día
                </p>
              </div>
            </div>
          </motion.aside>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl shadow-navy/5 sm:p-10 lg:col-span-3"
          >
            <Link
              to="/"
              data-testid="presupuesto-back-link"
              className="mb-8 inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition-colors hover:text-ocean-600"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver a la página principal
            </Link>

            <h2 className="font-display text-xl font-bold text-navy sm:text-2xl">
              Datos para su presupuesto
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              Todos los campos son obligatorios para poder elaborar su propuesta con precisión.
            </p>

            <form onSubmit={onSubmit} className="mt-8 grid gap-6 sm:grid-cols-2" data-testid="presupuesto-form" noValidate>
              <div className="sm:col-span-2">
                <Field label="Nombre completo" testid="input-nombre" error={errors.nombre}>
                  <input
                    type="text"
                    data-testid="input-nombre"
                    value={form.nombre}
                    onChange={set("nombre")}
                    placeholder="Ej. Juan Pérez"
                    className={inputCls}
                  />
                </Field>
              </div>

              <div className="sm:col-span-2">
                <Field label="Número y tipo de equipos" testid="select-numero-equipos" error={errors.numero_equipos}>
                  <select
                    data-testid="select-numero-equipos"
                    value={form.numero_equipos}
                    onChange={set("numero_equipos")}
                    className={`${inputCls} ${form.numero_equipos ? "" : "text-slate-400"}`}
                  >
                    <option value="" disabled>
                      Seleccione una opción (Ej: 1x1, 2x1, Conductos)
                    </option>
                    {EQUIPOS.map((op) => (
                      <option key={op} value={op} className="text-navy">
                        {op}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <div className="sm:col-span-2">
                <Field label="¿Ya tiene el aparato comprado?" testid="radio-aparato-comprado" error={errors.aparato_comprado}>
                  <div className="flex gap-3" role="radiogroup" aria-label="¿Ya tiene el aparato comprado?">
                    {["Sí", "No"].map((op) => {
                      const selected = form.aparato_comprado === op;
                      return (
                        <button
                          type="button"
                          key={op}
                          role="radio"
                          aria-checked={selected}
                          data-testid={`radio-aparato-comprado-${op === "Sí" ? "si" : "no"}`}
                          onClick={() => {
                            setForm((f) => ({ ...f, aparato_comprado: op }));
                            setErrors((er) => ({ ...er, aparato_comprado: undefined }));
                          }}
                          className={`flex-1 rounded-xl border px-4 py-3 text-sm font-semibold transition-all duration-300 ${
                            selected
                              ? "border-ocean-600 bg-ocean-600 text-white shadow-lg shadow-ocean-600/25"
                              : "border-slate-200 bg-white text-slate-600 hover:border-ocean-600/40 hover:text-ocean-700"
                          }`}
                        >
                          {op}
                        </button>
                      );
                    })}
                  </div>
                </Field>
              </div>

              <Field label="Ciudad" testid="input-ciudad" error={errors.ciudad}>
                <input
                  type="text"
                  data-testid="input-ciudad"
                  value={form.ciudad}
                  onChange={set("ciudad")}
                  placeholder="Ej. Madrid"
                  className={inputCls}
                />
              </Field>

              <Field label="Código Postal" testid="input-codigo-postal" error={errors.codigo_postal}>
                <input
                  type="text"
                  inputMode="numeric"
                  data-testid="input-codigo-postal"
                  value={form.codigo_postal}
                  onChange={set("codigo_postal")}
                  placeholder="Ej. 28001"
                  className={inputCls}
                />
              </Field>

              <Field label="Calle y número" testid="input-calle" error={errors.calle}>
                <input
                  type="text"
                  data-testid="input-calle"
                  value={form.calle}
                  onChange={set("calle")}
                  placeholder="Ej. Av. Principal 45, 2ºB"
                  className={inputCls}
                />
              </Field>

              <Field label="Número de teléfono" testid="input-telefono" error={errors.telefono}>
                <input
                  type="tel"
                  data-testid="input-telefono"
                  value={form.telefono}
                  onChange={set("telefono")}
                  placeholder="Ej. +34 600 000 000"
                  className={inputCls}
                />
              </Field>

              <div className="sm:col-span-2">
                <label
                  className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4"
                  data-testid="checkbox-privacidad-label"
                >
                  <input
                    type="checkbox"
                    checked={form.privacidad}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, privacidad: e.target.checked }));
                      setErrors((er) => ({ ...er, privacidad: undefined }));
                    }}
                    data-testid="checkbox-privacidad"
                    className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-slate-300 accent-ocean-600"
                  />
                  <span className="text-xs leading-relaxed text-slate-500">
                    He leído y acepto la{" "}
                    <Link to="/privacidad" data-testid="form-privacidad-link" className="font-semibold text-ocean-700 underline underline-offset-2">
                      Política de Privacidad
                    </Link>{" "}
                    y consiento el tratamiento de mis datos para la gestión de mi solicitud de
                    presupuesto, conforme al RGPD.
                  </span>
                </label>
                {errors.privacidad && (
                  <p className="mt-1.5 text-xs font-medium text-red-600" data-testid="checkbox-privacidad-error">
                    {errors.privacidad}
                  </p>
                )}
              </div>

              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="submit-budget-whatsapp-btn"
                  className="group inline-flex w-full items-center justify-center gap-3 rounded-full bg-ember px-8 py-4 text-sm font-semibold text-white shadow-xl shadow-ember/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-ember-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <MessageCircle className="h-5 w-5" />
                  {sending ? "Generando solicitud…" : "Enviar presupuesto por WhatsApp"}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <p className="mt-4 text-center text-xs leading-relaxed text-slate-400">
                  Al enviar, se abrirá WhatsApp con su solicitud lista para enviar al
                  número oficial de Clima Oliver ({BRAND.whatsappDisplay}).
                </p>
              </div>
            </form>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
